import { defineStore } from "pinia";
import { computed, toRefs, reactive } from "vue";
import type { IDomainGiftCardProvider } from "~/domain/gift-card-provider";
import {
  useBugReporter,
  useDirectBackend,
  type IDomainSalesChannel,
} from "#imports";

type IState = {
  isLoaded: boolean;
  outletId?: string;
  salesChannel?: IDomainSalesChannel;
  providers: IDomainGiftCardProvider[];
};

function createInitialState(): IState {
  return {
    isLoaded: false,
    outletId: undefined,
    salesChannel: undefined,
    providers: [],
  };
}

export const useGiftCardStore = defineStore("gift-card", () => {
  const state = reactive<IState>(createInitialState());

  const { report } = useBugReporter();

  async function loadProviders(
    outletId: string,
    salesChannel: IDomainSalesChannel,
  ) {
    if (
      state.isLoaded &&
      state.outletId === outletId &&
      state.salesChannel === salesChannel
    ) {
      // gift card providers are already loaded
      return;
    }

    try {
      const { giftCardRepo } = useDirectBackend();
      const response = await giftCardRepo.getProviders({
        outletId,
        salesChannel,
      });

      patch({
        isLoaded: true,
        outletId,
        salesChannel,
        providers: response,
      });
    } catch (e) {
      report(e);
    }
  }

  function reset() {
    Object.assign(state, createInitialState());
  }

  function patch(patch: Partial<IState>) {
    Object.assign(state, patch);
  }

  return {
    ...toRefs(state),
    state: computed(() => state),
    patch,
    loadProviders,
    reset,
  };
});
